// frontend/src/portal/pages/AdminTenantBranding.jsx
import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePortalAuth } from "../components/RequireClient.jsx";
import { apiFetch } from "../../api.js";
import ErrorBoundary from "../../components/ErrorBoundary.jsx";

const STORAGE_KEY = "tenant_branding_upload_state";

export default function AdminTenantBranding() {
  const { memberRole, tenant, tenantSlug } = usePortalAuth();
  const navigate = useNavigate();

  // ✅ GOOGLE-STYLE FIX: Use useRef for file state (persists across re-renders)
  const fileRef = useRef(null);
  const previewUrlRef = useRef(null);

  const [, forceUpdate] = useState({});
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ✅ FIX: Load current logo whenever tenant changes (on mount AND after upload)
  useEffect(() => {
    if (tenant?.logo_url) {
      console.log("[AdminTenantBranding] Loading logo from tenant:", tenant.logo_url);
      setCurrentLogoUrl(tenant.logo_url);
    } else {
      setCurrentLogoUrl(null);
    }
  }, [tenant?.logo_url]);

  // ✅ FIX: Restore file state from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        console.log("[AdminTenantBranding] Restoring state from sessionStorage:", data);
        if (data.previewUrl) {
          previewUrlRef.current = data.previewUrl;
          forceUpdate({});
        }
      }
    } catch (err) {
      console.error("[AdminTenantBranding] Failed to restore state:", err);
    }
  }, []);

  const isSuperAdmin = memberRole === "super_admin";
  const isTenantAdmin = ["owner", "admin"].includes(memberRole);
  const canManageBranding = isSuperAdmin || isTenantAdmin;

  const tenantLabel = useMemo(() => tenant?.name || tenantSlug || "your tenant", [tenant, tenantSlug]);

  const handleFileChange = (e) => {
    setError(null);
    setSuccess(null);

    const selected = e.target.files?.[0] || null;

    if (!selected) {
      fileRef.current = null;
      previewUrlRef.current = null;
      sessionStorage.removeItem(STORAGE_KEY);
      forceUpdate({});
      return;
    }

    if (!selected.type.startsWith("image/")) {
      setError("Please select an image file (PNG, JPEG, WebP, SVG, ICO, etc.).");
      fileRef.current = null;
      previewUrlRef.current = null;
      sessionStorage.removeItem(STORAGE_KEY);
      forceUpdate({});
      return;
    }

    fileRef.current = selected;

    const preview = URL.createObjectURL(selected);
    previewUrlRef.current = preview;

    // ✅ Save to sessionStorage as backup
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          fileName: selected.name,
          fileSize: selected.size,
          fileType: selected.type,
          previewUrl: preview,
          timestamp: Date.now(),
        })
      );
    } catch (err) {
      console.warn("[AdminTenantBranding] Failed to save to sessionStorage:", err);
    }

    forceUpdate({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantSlug) {
      setError("Tenant information is missing.");
      return;
    }

    if (!fileRef.current) {
      setError("Please choose a logo image first.");
      return;
    }

    const formData = new FormData();
    formData.append("logo", fileRef.current);

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const url = `/api/portal/tenants/${encodeURIComponent(tenantSlug)}/logo`;

      const res = await apiFetch(url, {
        method: "POST",
        body: formData,
        portal: true,
      });

      if (!res.ok) {
        let detail = "Failed to upload logo.";
        try {
          const data = await res.json();
          detail = data.detail || data.title || data.error || detail;
        } catch {}
        throw new Error(detail);
      }

      const data = await res.json();
      const logoUrl = data.logo_url || data.logoUrl || null;

      console.log("[AdminTenantBranding] Upload successful, logo URL:", logoUrl);

      // ✅ Update current logo immediately
      setCurrentLogoUrl(logoUrl);
      setSuccess("Logo updated successfully! Refresh the page to see it in the header.");

      // Clear file input and preview
      fileRef.current = null;
      previewUrlRef.current = null;
      sessionStorage.removeItem(STORAGE_KEY);

      const fileInput = document.getElementById("tenant-logo");
      if (fileInput) fileInput.value = "";

      forceUpdate({});
    } catch (err) {
      console.error("[AdminTenantBranding] Upload error:", err);
      setError(err.message || "Something went wrong while uploading the logo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!canManageBranding) {
    return (
      <ErrorBoundary componentName="AdminTenantBranding">
        <div style={{ maxWidth: "var(--container-max)", margin: "0 auto", padding: "var(--space-2xl) var(--space-xl)" }}>
          <h1 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "var(--space-md)" }}>Tenant branding</h1>
          <p style={{ color: "var(--text-muted)", marginBottom: "var(--space-lg)" }}>You don't have permission to change branding for this tenant.</p>
        </div>
      </ErrorBoundary>
    );
  }

  const file = fileRef.current;
  const previewUrl = previewUrlRef.current;

  return (
    <ErrorBoundary componentName="AdminTenantBranding">
      <div style={{ maxWidth: "var(--container-max-wide)", margin: "0 auto", padding: "var(--space-2xl) var(--space-xl)" }}>
        <button
          type="button"
          onClick={() => navigate("/portal/overview")}
          style={{
            marginBottom: "var(--space-lg)",
            border: "none",
            background: "transparent",
            color: "var(--text-secondary)",
            fontSize: "13px",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
          }}>
          <span style={{ fontSize: "16px" }}>←</span>
          Back to overview
        </button>

        <h1 style={{ fontSize: "26px", fontWeight: "600", marginBottom: "var(--space-xs)" }}>Tenant branding & logo</h1>
        <p style={{ color: "var(--text-muted)", marginBottom: "var(--space-xl)" }}>Update the logo that appears in the portal overview. Best results with SVG or PNG files.</p>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1.3fr)", gap: "var(--space-2xl)", alignItems: "flex-start" }}>
          <form
            onSubmit={handleSubmit}
            style={{
              backgroundColor: "var(--surface)",
              border: "1px solid var(--border-primary)",
              borderRadius: "var(--radius-lg)",
              padding: "var(--space-xl)",
              boxShadow: "var(--shadow-sm)",
            }}>
            <h2 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "var(--space-md)" }}>Upload logo for {tenantLabel}</h2>
            <p style={{ fontSize: "14px", color: "var(--text-muted)", marginBottom: "var(--space-lg)" }}>
              Recommended: SVG for best quality, or PNG at least 400×140 px. Horizontal logos work best.
            </p>

            <div style={{ marginBottom: "var(--space-lg)" }}>
              <label htmlFor="tenant-logo" style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "var(--space-xs)" }}>
                Logo image
              </label>
              <input id="tenant-logo" name="tenant-logo" type="file" accept="image/*" onChange={handleFileChange} style={{ fontSize: "14px" }} />
            </div>

            {previewUrl && (
              <div style={{ marginTop: "var(--space-md)", marginBottom: "var(--space-lg)" }}>
                <div style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "var(--space-xs)" }}>Preview</div>
                <div
                  style={{
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-secondary)",
                    padding: "var(--space-lg)",
                    backgroundColor: "var(--surface-elevated)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "120px",
                  }}>
                  <img src={previewUrl} alt="Logo preview" style={{ maxWidth: "100%", maxHeight: "100px", objectFit: "contain" }} />
                </div>
              </div>
            )}

            {error && (
              <div
                style={{
                  marginTop: "var(--space-md)",
                  marginBottom: "var(--space-md)",
                  padding: "12px 14px",
                  borderRadius: "var(--radius-md)",
                  backgroundColor: "var(--danger-bg)",
                  color: "var(--danger)",
                  fontSize: "13px",
                  border: "1px solid var(--danger)",
                }}>
                {error}
              </div>
            )}

            {success && (
              <div
                style={{
                  marginTop: "var(--space-md)",
                  marginBottom: "var(--space-md)",
                  padding: "12px 14px",
                  borderRadius: "var(--radius-md)",
                  backgroundColor: "var(--success-bg)",
                  color: "var(--success)",
                  fontSize: "13px",
                  border: "1px solid var(--success)",
                }}>
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !file}
              style={{
                marginTop: "var(--space-lg)",
                padding: "10px 20px",
                borderRadius: "var(--radius-md)",
                border: "none",
                fontSize: "14px",
                fontWeight: "600",
                cursor: submitting || !file ? "not-allowed" : "pointer",
                opacity: submitting || !file ? 0.5 : 1,
                backgroundColor: "var(--accent-primary)",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                transition: "all var(--transition-fast)",
              }}>
              {submitting ? "Uploading…" : "Save logo"}
            </button>
          </form>

          <div
            style={{
              backgroundColor: "var(--surface)",
              border: "1px solid var(--border-primary)",
              borderRadius: "var(--radius-lg)",
              padding: "var(--space-xl)",
              boxShadow: "var(--shadow-sm)",
            }}>
            <h2 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "var(--space-md)" }}>Current logo</h2>
            {currentLogoUrl ? (
              <div
                style={{
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border-secondary)",
                  padding: "var(--space-lg)",
                  backgroundColor: "var(--surface-elevated)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "120px",
                }}>
                <img src={currentLogoUrl} alt="Current tenant logo" style={{ maxWidth: "100%", maxHeight: "100px", objectFit: "contain" }} />
              </div>
            ) : (
              <p style={{ color: "var(--text-muted)", fontSize: "14px" }}>No logo uploaded yet. Once you upload one, it will appear here and on the portal overview page.</p>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}
